/**
 * VantageTrend Store
 *
 * Manages the state for the VantageTrend panel, including QuickTrend visibility,
 * axis mode, Gantt chart display, and context capsule visibility.
 */

import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@/stores/createStore';
import type { Moment } from 'moment';

export interface PendingContextRequest {
  start: Moment;
  end: Moment;
  adjustedContextKind: string;
}

export interface VantageTrendState {
  /** Whether the QuickTrend panel is open. */
  quickTrendOpen: boolean;
  /** Whether the trend chart uses a single shared Y-axis. */
  isSingleAxis: boolean;
  /** Whether to show all conditions in the Gantt chart view. */
  ganttShowAll: boolean;
  /** Whether context capsules are displayed on the trend. */
  showContextCapsules: boolean;
  /** Transient signal from QuickTrend to the context console requesting that a drag-selected
   * time range be applied as the staged-context override and that the note input be focused.
   * Consumed and drained (set back to undefined) by VantageConsole. Not persisted. */
  pendingContextRequest: PendingContextRequest | undefined;
}

export interface VantageTrendActions {
  setQuickTrendOpen: (isOpen: boolean) => void;
  setSingleAxis: (isSingleAxis: boolean) => void;
  setGanttShowAll: (showAll: boolean) => void;
  setShowContextCapsules: (showContextCapsules: boolean) => void;
  requestAddContext: (request: PendingContextRequest | undefined) => void;
  reset: () => void;
}

const initialState: VantageTrendState = {
  quickTrendOpen: false,
  isSingleAxis: true,
  ganttShowAll: false,
  showContextCapsules: false,
  pendingContextRequest: undefined,
};

export type VantageTrendDehydratedState = Omit<VantageTrendState, 'pendingContextRequest'>;

export const dehydrateVantageTrendState = (state: VantageTrendState): VantageTrendDehydratedState => ({
  quickTrendOpen: state.quickTrendOpen,
  isSingleAxis: state.isSingleAxis,
  ganttShowAll: state.ganttShowAll,
  showContextCapsules: state.showContextCapsules,
});

const vantageTrendActionsFactory: ActionsFactory<VantageTrendState, VantageTrendActions> = (set, _get, resetState) => ({
  setQuickTrendOpen: (isOpen) =>
    set((draft) => {
      draft.quickTrendOpen = isOpen;
    }),

  setSingleAxis: (isSingleAxis) =>
    set((draft) => {
      draft.isSingleAxis = isSingleAxis;
    }),

  setGanttShowAll: (showAll) =>
    set((draft) => {
      draft.ganttShowAll = showAll;
    }),

  setShowContextCapsules: (showContextCapsules) =>
    set((draft) => {
      draft.showContextCapsules = showContextCapsules;
    }),

  requestAddContext: (request) =>
    set((draft) => {
      draft.pendingContextRequest = request;
    }),

  reset: () => {
    resetState();
  },
});

const [VantageTrendStoreProvider, useVantageTrendStoreInstance, useVantageTrendSelector] = createStore(
  'VantageTrend',
  initialState,
  vantageTrendActionsFactory,
);

export { VantageTrendStoreProvider };

export const useQuickTrendOpen = () => useVantageTrendSelector((state) => state.quickTrendOpen);
export const useIsSingleAxis = () => useVantageTrendSelector((state) => state.isSingleAxis);
export const useGanttShowAll = () => useVantageTrendSelector((state) => state.ganttShowAll);
export const useShowContextCapsules = () => useVantageTrendSelector((state) => state.showContextCapsules);
export const usePendingContextRequest = () => useVantageTrendSelector((state) => state.pendingContextRequest);
export const useVantageTrendActions = () => useVantageTrendStoreInstance().actions;

/**
 * Creates a standalone store instance for tests that need imperative state control
 * (mid-test mutations via `actions.*` or assertions via `getState()`).
 * Prefer `initialVantageTrendData` on `TestStoreProvider` when only setting initial state.
 */
export function createTestVantageTrendStore(overrides?: Partial<VantageTrendState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestVantageTrendStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, vantageTrendActionsFactory);
}

/**
 * Registers the VantageTrend store with the persistence system.
 * Uses the legacy store name 'sqVantageTrendStore' for workstep compatibility.
 */
export const usePersistVantageTrendStore = () => {
  const register = useRegisterPersistedStore();
  const store = useVantageTrendStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqVantageTrendStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateVantageTrendState,
    });
  }, [register, store]);
};
